// LaunchDayCheck — Jun 2026 (Randy). Step 5 of the Go-Live wizard.
//
// The wizard above proves the platform is CONFIGURED. This proves it WORKED
// once real money moved: Stripe reached us, every payment carries the charge id
// the refund chain depends on, a real refund clawed back credit + access +
// commission, the notices left the building, and the payouts landed.
//
// Read-only. It buys nothing and refunds nothing — Randy does the buying with
// his own card and this reads the evidence. It is deliberately loud about what
// he has NOT rehearsed yet, because "no refunds seen" is a very different
// answer from "refunds worked".
import { useState } from "react";
import { api } from "@/lib/api";
import {
  CheckCircle, WarningCircle, XCircle, MinusCircle, Rocket, ArrowClockwise, CaretRight,
} from "@phosphor-icons/react";

const STATUS = {
  pass: { Icon: CheckCircle, cls: "text-[#2F6B3A]", pill: "bg-[#E8F0E9] text-[#2F6B3A]", label: "pass" },
  attention: { Icon: WarningCircle, cls: "text-[#A8662F]", pill: "bg-[#FBF1ED] text-[#A8662F]", label: "look" },
  fail: { Icon: XCircle, cls: "text-[#A03D22]", pill: "bg-[#F6E2DA] text-[#A03D22]", label: "fail" },
  skip: { Icon: MinusCircle, cls: "text-[#9C9A94]", pill: "bg-[#F0F1EE] text-[#6B6A68]", label: "untested" },
};

const VERDICT = {
  pass: ["#E8F0E9", "#2F6B3A", "Everything that moved money worked."],
  attention: ["#FBF1ED", "#A8662F", "Working, with things worth your eyes."],
  fail: ["#F6E2DA", "#A03D22", "Something in the money path is broken."],
};

const CheckRow = ({ c }) => {
  const s = STATUS[c.status] || STATUS.skip;
  const { Icon } = s;
  return (
    <li className="flex gap-2.5 py-2.5 border-b border-[#EFEDE7] last:border-0"
      data-testid={`ldc-check-${c.id}`}>
      <Icon size={16} weight="fill" className={`${s.cls} shrink-0 mt-0.5`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-start gap-2 flex-wrap">
          <span className="text-[12px] text-[#1C1B1A] font-medium">{c.title}</span>
          <span className={`text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded ${s.pill}`}
            data-testid={`ldc-status-${c.id}`}>
            {s.label}
          </span>
        </div>
        <div className="text-[11px] text-[#6B6A68] leading-relaxed mt-0.5">{c.detail}</div>
        {c.fix && c.status !== "pass" && (
          <div className="text-[11px] text-[#4A5D4E] leading-relaxed mt-1">
            <strong className="text-[#6B6A68] font-normal">Do this:</strong> {c.fix}
          </div>
        )}
      </div>
    </li>
  );
};

export default function LaunchDayCheck() {
  const [open, setOpen] = useState(false);
  const [hours, setHours] = useState(24);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const run = async () => {
    setBusy(true);
    setErr("");
    try {
      const r = await api.post(`/admin/reservoir/go-live/launch-day-check?hours=${hours}`, {});
      setReport(r.data);
      setOpen(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not run the check.");
    } finally {
      setBusy(false);
    }
  };

  const [bg, fg, verdictText] = VERDICT[report?.overall] || VERDICT.attention;

  return (
    <div className="bg-white border hairline rounded-lg p-4" data-testid="launch-day-check">
      <div className="flex items-start gap-2.5">
        <Rocket size={17} weight="duotone" className="text-[#4A5D4E] shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <h4 className="font-serif-display text-base text-[#1C1B1A]">One-click launch-day check</h4>
          <p className="text-[11px] text-[#6B6A68] leading-relaxed mt-0.5">
            Run this after your first real purchases. It reads what actually happened — whether
            Stripe reached us, whether every payment carries the charge id refunds depend on,
            whether a refund really clawed back credit, access and commission, whether the notices
            sent, and whether the payouts landed. It buys nothing and refunds nothing.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3">
        <select
          value={hours}
          onChange={(e) => setHours(Number(e.target.value))}
          data-testid="ldc-window-select"
          className="text-[11px] border hairline rounded px-2 py-1.5 bg-white text-[#1C1B1A]"
        >
          <option value={1}>last hour</option>
          <option value={24}>last 24 hours</option>
          <option value={72}>last 3 days</option>
          <option value={168}>last 7 days</option>
          <option value={720}>last 30 days</option>
        </select>
        <button
          onClick={run}
          disabled={busy}
          data-testid="ldc-run-btn"
          className="text-[11px] px-3 py-1.5 rounded bg-[#1C1B1A] text-white hover:bg-[#333] disabled:opacity-50 flex items-center gap-1.5"
        >
          <ArrowClockwise size={12} className={busy ? "animate-spin" : ""} />
          {busy ? "Checking…" : report ? "Run again" : "Run the check"}
        </button>
        {report && (
          <span className="text-[10px] text-[#9C9A94]" data-testid="ldc-generated-at">
            {new Date(report.generated_at).toLocaleString()}
          </span>
        )}
      </div>

      {err && (
        <p className="text-[11px] text-[#A03D22] mt-2" data-testid="ldc-error">{err}</p>
      )}

      {report && (
        <>
          <div
            className="mt-3 rounded-md px-3.5 py-3"
            style={{ background: bg }}
            data-testid="ldc-verdict"
          >
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-[10px] uppercase tracking-[0.2em]" style={{ color: fg }}>
                {report.rehearsal ? "Rehearsal · test keys" : "Live"}
              </span>
              <span className="font-serif-display text-[15px] text-[#1C1B1A]">{verdictText}</span>
            </div>
            <div className="text-[11px] text-[#6B6A68] mt-1 tabular-nums">
              {report.counts.pass} passed · {report.counts.attention} to look at ·{" "}
              {report.counts.fail} failing · {report.counts.skip} not yet exercised
              {report.missing_event_types?.length > 0 && (
                <>
                  {" "}· <span style={{ color: fg }}>
                    no {report.missing_event_types.join(", ")} seen in this window
                  </span>
                </>
              )}
            </div>
          </div>

          {/* What has and hasn't been rehearsed — the honest bit. */}
          <div className="mt-3" data-testid="ldc-coverage">
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#4A5D4E] mb-1.5">
              Rehearsal coverage
            </div>
            <div className="flex flex-wrap gap-1.5">
              {report.coverage.map((c) => (
                <span
                  key={c.id}
                  data-testid={`ldc-coverage-${c.id}`}
                  className={`text-[10px] px-2 py-1 rounded-full border ${
                    c.done
                      ? "bg-[#E8F0E9] text-[#2F6B3A] border-[#CADCCE]"
                      : "bg-white text-[#9C9A94] border-[#E4E2DC] border-dashed"
                  }`}
                >
                  {c.done ? "✓ " : "○ "}{c.label}
                </span>
              ))}
            </div>
          </div>

          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="ldc-details-toggle"
            aria-expanded={open}
            className="mt-3 flex items-center gap-1.5 text-[11px] text-[#4A5D4E] hover:text-[#1C1B1A]"
          >
            <CaretRight size={11} weight="bold"
              className={`transition-transform duration-200 ${open ? "rotate-90" : ""}`} />
            {open ? "Hide" : "Show"} all {report.checks.length} checks
          </button>

          {open && (
            <ul className="mt-2 border hairline rounded-md px-3 bg-[#FDFCF8]"
              data-testid="ldc-check-list">
              {report.checks.map((c) => <CheckRow key={c.id} c={c} />)}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
